/**
 * App.ts — Root shell composition.
 *
 * Assembles TopBar, ModuleViewport, and BottomNav into the app layout.
 * Listens to router changes and swaps the active module in the viewport.
 */

import type { AweniContext } from './context/AweniContext';
import type { Router } from './router';
import { mountTopBar } from './shell/TopBar';
import { mountBottomNav, setActiveTab, toggleMoreMenu } from './shell/BottomNav';
import { mountModuleViewport, setActiveModule } from './shell/ModuleViewport';
import { mountNotificationDrawer, openNotificationDrawer } from './shell/NotificationDrawer';
import { mountProfileSwitcher, toggleProfileSwitcher } from './shell/ProfileSwitcher';
import { getModuleById, getBottomNavModules } from './modules/registry';
import { mountFooter } from './shell/Footer';

export interface MountAppOptions {
  /** When 'minimal', skip TopBar / BottomNav / Footer / NotificationDrawer /
   *  ProfileSwitcher and mount only the module viewport. Used by standalone
   *  /hisni/ and /yawmi/ URLs (`?shell=minimal`) so search engines + sharing
   *  links serve the AWENI bundle without AWENI chrome. See ST3 spec
   *  + docs/MODULE-INTEGRATION.md. */
  shellMode?: 'full' | 'minimal';
}

/**
 * Mount the full app shell into the given container.
 */
export function mountApp(root: HTMLElement, ctx: AweniContext, router: Router, opts: MountAppOptions = {}): void {
  root.innerHTML = '';
  const isMinimal = opts.shellMode === 'minimal';

  // Accessibility skip link
  const skipLink = document.createElement('a');
  skipLink.href = '#aweni-main';
  skipLink.className = 'skip-link';
  skipLink.textContent = ctx.i18n.t('skip_to_content') || 'Skip to content';
  root.appendChild(skipLink);

  // Top bar
  // SHELL-LAYOUT-ORDER: 1
  const topBarEl = document.createElement('header');
  topBarEl.id = 'aweni-topbar';
  root.appendChild(topBarEl);

  // Notification drawer (off-screen overlay)
  const drawerEl = document.createElement('aside');
  drawerEl.id = 'aweni-notifications';
  root.appendChild(drawerEl);

  // Profile switcher (off-screen overlay)
  const profileEl = document.createElement('aside');
  profileEl.id = 'aweni-profile-switcher';
  root.appendChild(profileEl);

  // Module viewport (main content area)
  // SHELL-LAYOUT-ORDER: 2
  const viewportEl = document.createElement('main');
  viewportEl.id = 'aweni-main';
  viewportEl.setAttribute('role', 'main');
  viewportEl.className = 'module-viewport';
  viewportEl.style.cssText = 'flex: 1; overflow-y: auto; overflow-x: hidden;';
  root.appendChild(viewportEl);

  // Footer — sits OUTSIDE the scrollable viewport, between viewport and
  // bottom nav. (When it was mounted inside viewport it appeared at the TOP
  // of the page in DOM-order block flow because module containers are appended
  // after it. See feedback memory; reverted here.)
  // SHELL-LAYOUT-ORDER: 3
  const footerEl = document.createElement('footer');
  footerEl.id = 'aweni-footer';
  root.appendChild(footerEl);
  mountFooter(footerEl, ctx);

  // Bottom nav
  // SHELL-LAYOUT-ORDER: 4
  const bottomNavEl = document.createElement('nav');
  bottomNavEl.id = 'aweni-bottomnav';
  root.appendChild(bottomNavEl);

  // Mount shell components
  // SHELL-LAYOUT-ORDER: 5
  if (!isMinimal) {
    mountTopBar(topBarEl, ctx, {
      onNotificationsClick: () => openNotificationDrawer(),
      onProfileClick: () => toggleProfileSwitcher(),
    });

    mountNotificationDrawer(drawerEl, ctx);
    mountProfileSwitcher(profileEl, ctx);
  } else {
    // Minimal shell: hide all chrome elements, show only viewport.
    topBarEl.style.display = 'none';
    drawerEl.style.display = 'none';
    profileEl.style.display = 'none';
    footerEl.style.display = 'none';
    bottomNavEl.style.display = 'none';
    viewportEl.style.paddingTop = '0';
    viewportEl.style.paddingBottom = '0';
  }
  mountModuleViewport(viewportEl, ctx);

  if (!isMinimal) {
    const navModules = getBottomNavModules();
    mountBottomNav(bottomNavEl, ctx, navModules, (moduleId: string) => {
      router.navigate(`/${moduleId}`);
    });
  }

  // Auth routes that should render full-screen (no shell chrome)
  const FULL_SCREEN_MODULES = new Set(['auth']);

  // React to route changes
  router.onChange((route) => {
    const moduleId = route.module || 'home';
    // The "more" route (= user clicked the More door from the home grid OR
    // the More tab in bottom nav) opens the dropdown menu instead of
    // mounting a module. Without this, clicking the More door changes the
    // URL to /aweni/#/more but the page does nothing visible — looks broken.
    if (moduleId === 'more') {
      toggleMoreMenu();
      // Don't update the active tab — keep whatever was active. After the
      // user picks a sub-item from the menu, the menu navigates with the
      // proper sub-module and that route fires again normally.
      return;
    }

    // Full-screen mode for auth (hide shell chrome). Critical: when we restore
    // visibility, we MUST set the original display value (flex for the nav,
    // block for the others) instead of `''`. Setting `style.display = ''`
    // *removes* the property from the inline `cssText`, so the bottom nav
    // falls back to `<nav>`'s default `display: block` and the 5 tabs (each
    // with `flex: 1`) collapse to width=0 stacked at x=0 — visually only
    // "Home" with its green active label is readable. Bug observed in
    // production after the entry → home transition: console reported the
    // home route loaded fine but the nav looked like a single tab. Confirmed
    // via Playwright DOM probe — 5 children present at x=0 each.
    if (FULL_SCREEN_MODULES.has(moduleId) || isMinimal) {
      topBarEl.style.display = 'none';
      bottomNavEl.style.display = 'none';
      viewportEl.style.paddingTop = '0';
      viewportEl.style.paddingBottom = '0';
      footerEl.style.display = 'none';
    } else {
      topBarEl.style.display = 'flex';
      bottomNavEl.style.display = 'flex';
      viewportEl.style.paddingTop = '';
      viewportEl.style.paddingBottom = '';
      footerEl.style.display = '';
    }

    const moduleDef = getModuleById(moduleId);
    if (moduleDef) {
      setActiveModule(moduleId, ctx, route.subpath);
      setActiveTab(moduleId);
    }
  });

  // Navigate to current hash path or default to home / entry.
  // Unauthenticated users land on the 3-button entry screen (Guest / Login / Sign up).
  const hash = window.location.hash.replace(/^#/, '');
  const isAuthRoute =
    /^\/(entry|guest|claim|login|signup|forgot-password|reset-password|verify-email)\b/.test(hash);
  let initialPath: string;
  if (hash) {
    initialPath = hash;
  } else if (ctx.auth.isAuthenticated) {
    initialPath = '/home';
  } else {
    initialPath = '/entry';
  }
  // Edge case: explicit /home or empty for an unauthenticated user → entry
  if (!ctx.auth.isAuthenticated && !isAuthRoute && (hash === '' || /^\/home\b/.test(hash))) {
    initialPath = '/entry';
  }
  router.navigate(initialPath);
}
